// src/components/UnityPlayer.js
import React, { useEffect } from "react";

// Add this at the top of your file
declare global {
  interface Window {
    unityInstance: any;
    createUnityInstance: any;
  }
}

const BUILD_NAME = "TestBuildVRIK244";
const BUILD_EXT = ".unityweb";   // ".unityweb" for Brotli builds with the decompression fallback (VRIK226+; deploy.sh sets it)
(window as any).AOTS_BUILD = BUILD_NAME;

// Flag-aware corner tag: full build name for dev, discreet version for the
// real deploy (first-deploy polish, 2026-09-02)
const BuildTag = () => {
  const [label, setLabel] = React.useState("");
  React.useEffect(() => {
    fetch("levels/flags.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((f) => {
        if (!f || f.showBuildTag) setLabel(BUILD_NAME);
        else setLabel(f.version ? "v" + f.version : "");
      })
      .catch(() => setLabel(BUILD_NAME));
  }, []);
  // hidden mid-run (Joshua 2026-11-04 declutter)
  const [running, setRunning] = React.useState(false);
  React.useEffect(() => {
    const on = (e: Event) => {
      try {
        const ev = JSON.parse((e as CustomEvent).detail.j).event;
        if (ev === "run_start") setRunning(true);
        else if (ev === "run_end" || ev === "run_abort" || ev === "quest_end" || ev === "menu") setRunning(false);
      } catch { /* not ours */ }
    };
    window.addEventListener("telemetry", on);
    return () => window.removeEventListener("telemetry", on);
  }, []);
  if (!label || running) return null;
  return (
    <div style={{ position: "absolute", left: 6, bottom: 4, fontSize: 10,
                  color: "rgba(255,255,255,0.55)", pointerEvents: "none",
                  fontFamily: "monospace", background: "rgba(0,0,0,0.55)",
                  padding: "1px 6px", borderRadius: 4 }}>
      {label}
    </div>
  );
};

const UnityPlayer = () => {
  useEffect(() => {
    const script = document.createElement("script");
    // ?v: a fresh URL - a pre-deploy probe got the SPA 404 page cached at the bare path for a year (2026-10-25)
    script.src = `${process.env.PUBLIC_URL}/unity/${BUILD_NAME}.loader.js?v=1`;
    script.onload = () => {
      window
        .createUnityInstance(
          document.querySelector("#unity-canvas") as HTMLCanvasElement,
          {
            dataUrl: `${process.env.PUBLIC_URL}/unity/${BUILD_NAME}.data${BUILD_EXT}`,
            frameworkUrl: `${process.env.PUBLIC_URL}/unity/${BUILD_NAME}.framework.js${BUILD_EXT}`,
            codeUrl: `${process.env.PUBLIC_URL}/unity/${BUILD_NAME}.wasm${BUILD_EXT}`,
            streamingAssetsUrl: "StreamingAssets",
            companyName: "YourCompanyName",
            productName: "YourProductName",
            productVersion: "1.0",
            // Phones report DPR 3: that's 9x the pixels of 1x, and the game
            // measured ~12fps on an iPhone because of it. 1.5 quarters the
            // pixel count; the 3D scene barely loses sharpness.
            devicePixelRatio: Math.min(window.devicePixelRatio || 1, 1.5),
          }
        )
        .then((unityInstance) => {
          window.unityInstance = unityInstance;
          // how much of the top React owns: no FULLSCREEN pill on iPhone / the
          // home-screen app, so the game's HUD may start higher (VRIK228)
          try {
            const ios = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
            const standalone = (window.matchMedia && (window.matchMedia("(display-mode: standalone)").matches || window.matchMedia("(display-mode: fullscreen)").matches)) || (navigator as unknown as { standalone?: boolean }).standalone === true;
            setTimeout(() => { try { unityInstance.SendMessage("Manager", "HudTopFrac", ios || standalone ? 0.045 : 0.10); } catch { /* not ready */ } }, 1500);
          } catch { /* ignore */ }

          // Listen for pose data events
          // Player 2's stream (2P modes) — same format, separate channel
          window.addEventListener("poseData2", (event: Event) => {
            const customEvent = event as CustomEvent;
            const unityInstance = (window as any).unityInstance;
            if (unityInstance && unityInstance.SendMessage) {
              unityInstance.SendMessage("Manager", "printData2",
                JSON.stringify((customEvent.detail as number[]).flat()));
            }
          });
          window.addEventListener("poseData", (event: Event) => {
            const customEvent = event as CustomEvent;
            //console.log(customEvent.detail);
            const poseData = customEvent.detail;
            // Send pose data to Unity
            if (unityInstance && unityInstance.SendMessage) {
              // Serialize the pose data to JSON string. No logging: stringifying
              // to the console ~25x/sec measurably costs CPU on the phone.
              const serializedData = JSON.stringify(poseData.flat());
              unityInstance.SendMessage("Manager", "printData", serializedData);
            }
          });
        });
    };
    document.body.appendChild(script);
  }, []);

  // WebAudio keeps running when Safari is backgrounded, so the music would
  // play over whatever the user switched to. Mute on hide, restore on return.
  useEffect(() => {
    const setMuted = (muted: boolean) => {
      const inst = window.unityInstance;
      if (inst?.SendMessage) inst.SendMessage("Manager", "SetAudioMuted", muted ? "1" : "0");
    };
    const onVisibility = () => setMuted(document.hidden);
    const onHide = () => setMuted(true);
    const onShow = () => setMuted(false);

    // Only page-level visibility, never window blur/focus. On iOS blur fires for
    // things that aren't leaving the page at all — the camera permission prompt,
    // the address bar, a notification banner — and if no focus follows, the game
    // stays muted for the rest of the session.
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onHide);   // iOS fires this, not always visibilitychange
    window.addEventListener("pageshow", onShow);

    // Clear any stuck mute once Unity is up, whatever happened during load
    const unstick = window.setInterval(() => {
      if (window.unityInstance?.SendMessage) {
        setMuted(document.hidden);
        window.clearInterval(unstick);
      }
    }, 500);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("pageshow", onShow);
      window.clearInterval(unstick);
    };
  }, []);

  return (
    <div id="unity-container" style={{ position: "relative" }}>
      <canvas id="unity-canvas"></canvas>
      <BuildTag />
    </div>
  );
};

export default UnityPlayer;
